import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import camelcaseKeys from 'camelcase-keys';
import classNames from 'classnames';
import sanitizeHtml from 'sanitize-html';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import queryString from 'query-string';
import percentIcon from '@src/assets/icons/stroke-discount.png';
import { checkVietNam } from '@src/utils/checkCountry';
import apis from '@src/apis';
import {
  PACK_DURATION,
  PACKAGE_LEVEL,
  PACKAGE_TYPE,
  RESOURCES_TYPE,
} from '@src/constants/package';
import actions from '@src/redux/actions';
import {
  Box,
  Button,
  FormControlLabel,
  RadioGroup,
  Skeleton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { LANGUAGE } from '@src/constants';
import CustomRadio from '@src/components/CustomRadio';
import { COLOR } from '@src/styles/color';
import { BREAKPOINTS } from '@src/styles/config';
import theme from '@src/styles/theme';
import {
  PAYMENT_HUB_RENEWAL_TYPE,
  SUBSCRIPTION_PURCHASE_STATUS,
} from '@src/constants/subscription';
import { PAYMENT_METHOD_TYPE } from '@src/constants/payment';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import {
  checkCustomPackage,
  getDefaultValueCustomResource,
  getPackageDataDuration,
  roundSaleOffPercentage,
} from '@src/services/package';
import Tooltip from '@src/components/Tooltip';
import { useCheckDubbingRoute } from '@src/hooks/useCheckDubbingRoute';
import { StyledPackageInfoItem, StyledPackageInfo } from './index.style';
import BuyExtracharacters from './BuyExtracharacters';
import CustomResources from './CustomResources';

const getSubscriptionPeriod = (duration, isAutoRenew) => {
  const periodMap = {
    [PACK_DURATION.MONTH]: {
      oneTime: 'oneMonth',
      autoRenew: 'monthly',
    },
    [PACK_DURATION.YEAR]: {
      oneTime: 'oneYear',
      autoRenew: 'yearly',
    },
  };

  const translationKey =
    periodMap[duration]?.[isAutoRenew ? 'autoRenew' : 'oneTime'];
  return translationKey || '';
};

const PackageDuration = ({
  showTooltip,
  disabledPackageAutoRenewUsing,
  subscriptionPeriodKey,
  actualPrice,
  handleShowPopupCannotBuyPackage,
  saleOffPercentageRounded,
  hasSaleOff,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.SMD));
  const isVietNam = checkVietNam();

  const Price = ({
    price,
    lineThrough = false,
    duration = '',
    isOriginalPrice = false,
  }) => (
    <div className="price">
      {isVietNam && (
        <span
          className={`underline ${
            lineThrough || duration || isOriginalPrice
              ? 'original-price-unit font-size-14'
              : 'discount-price-unit font-size-16'
          }`}
        >
          đ
        </span>
      )}
      {!isVietNam && (
        <span
          className={`${
            lineThrough || duration || isOriginalPrice
              ? 'original-price-unit font-size-14'
              : 'discount-price-unit font-size-14'
          }`}
        >
          $
        </span>
      )}
      {isOriginalPrice ? (
        <span
          className={`original-price ${lineThrough ? 'line-through ' : ''}`}
        >
          {`${price?.toLocaleString(language)}${
            duration.length > 0 ? `/${duration}` : ''
          }`}
        </span>
      ) : (
        <span className="discount-price">
          {price?.toLocaleString(language)}
        </span>
      )}
    </div>
  );

  const PackageNameMobile = () => (
    <div className="title-wrapper">
      <div className="flex flex-col">
        <div className="flex align-item-center">
          <div className="title">{t(subscriptionPeriodKey)}</div>
          {hasSaleOff && !disabledPackageAutoRenewUsing && (
            <div className="tag-discount">
              <img
                src={percentIcon}
                className="percent-icon"
                alt="percent-icon"
              />
              {t('percentDiscount', {
                percentDiscount: saleOffPercentageRounded,
              })}
            </div>
          )}
        </div>
      </div>
      {showTooltip && (
        <Tooltip arrow placement="top">
          <InfoRoundedIcon
            onClick={(e) => {
              e.stopPropagation();
              handleShowPopupCannotBuyPackage();
            }}
            fontSize="16px"
            className="icon-info-rounded"
          />
        </Tooltip>
      )}
    </div>
  );

  const PackageName = () => (
    <div className="title-wrapper">
      <div className="title">{t(subscriptionPeriodKey)}</div>
      <>
        {hasSaleOff && !disabledPackageAutoRenewUsing && (
          <div className="tag-discount">
            <img
              src={percentIcon}
              className="percent-icon"
              alt="percent-icon"
            />
            {t('savePercentSuffix', {
              discount: saleOffPercentageRounded,
              suffix: '%',
            })}
          </div>
        )}
      </>
      {showTooltip && (
        <Tooltip
          arrow
          placement="top"
          title={
            <div
              role="button"
              tabIndex="0"
              onClick={(e) => e.stopPropagation()}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  t('goToMyPackage', {
                    color: '#fff',
                  }),
                  {
                    allowedTags: ['a', 'div'],
                    allowedAttributes: {
                      div: ['style'],
                      a: ['href', 'target', 'style'],
                    },
                  },
                ),
              }}
            />
          }
        >
          <InfoRoundedIcon
            onClick={(e) => {
              e.stopPropagation();
              handleShowPopupCannotBuyPackage();
            }}
            fontSize="16px"
            className="icon-info-rounded"
          />
        </Tooltip>
      )}
    </div>
  );

  const PackagePriceMonth = () => (
    <div className="price-wrapper">
      <Price price={actualPrice} />
    </div>
  );

  return (
    <StyledPackageInfoItem isMobile={isMobile}>
      <div
        className={classNames('package-info-title-and-price', {
          'align-item-center': isMobile,
        })}
      >
        {isMobile ? <PackageNameMobile /> : <PackageName />}
      </div>
      {disabledPackageAutoRenewUsing ? (
        <div className="using-text">{t('using')}</div>
      ) : (
        <div className="flex-col align-item-end">
          <PackagePriceMonth />
        </div>
      )}
    </StyledPackageInfoItem>
  );
};

const PackageDurationSelect = ({
  usingOrders,
  getPrice,
  sameLevelPackages,
  onSelectPackage,
  selectedPackage,
}) => {
  const getPackageKey = (code, isAutoRenew) => `${code}-${isAutoRenew}`;

  const [openPopupCannotBuyPackage, setOpenPopupCannotBuyPackage] =
    useState(false);

  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.SMD));

  const handleShowPopupCannotBuyPackage = () => {
    if (!isMobile) return;
    setOpenPopupCannotBuyPackage(true);
  };

  return (
    <div className="package-duration-select">
      <RadioGroup
        value={getPackageKey(
          selectedPackage?.code,
          selectedPackage?.isAutoRenew,
        )}
        aria-labelledby="row-radio-buttons-group-package-duration"
        name="row-radio-buttons-group"
        className="flex-col"
      >
        {sameLevelPackages?.map((pkg) => {
          const { actualPrice, saleOffPercentageRounded } = getPrice(pkg) || {};

          const subscriptionPeriodKey = getSubscriptionPeriod(
            pkg?.expiresIn,
            pkg?.isAutoRenew,
          );

          const hasSaleOff = saleOffPercentageRounded > 0;

          const { usingPackage, subscriptionPurchase } = useSelector(
            (state) => state.user || {},
          );
          const { user } = useSelector((state) => state.auth) || {};
          const usingStudioOrder = usingOrders?.find(
            (order) => order?.id === user?.orderId,
          );
          const renewwalType =
            usingStudioOrder?.renewalType ===
            PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW;
          const hasSamePackage =
            (usingPackage?.level === pkg?.level &&
              usingPackage?.type === pkg?.type &&
              usingPackage?.expiresIn === pkg?.expiresIn) ||
            usingPackage?.code === pkg?.code;
          const isAutoRenewSubscription = pkg?.isAutoRenew;
          const activeAutoRenew =
            subscriptionPurchase?.status ===
            SUBSCRIPTION_PURCHASE_STATUS.ACTIVE;
          const isBuyOnApp = [
            PAYMENT_METHOD_TYPE.APPLE,
            PAYMENT_METHOD_TYPE.GOOGLE,
          ].includes(usingStudioOrder?.paymentMethod?.type);

          const samePackage =
            hasSamePackage && isAutoRenewSubscription && renewwalType;

          let disabledPackageAutoRenewUsing = false;
          if (isBuyOnApp && samePackage) {
            disabledPackageAutoRenewUsing = activeAutoRenew;
          }
          if (!isBuyOnApp && samePackage) {
            disabledPackageAutoRenewUsing = !!subscriptionPurchase;
          }

          return (
            <Box
              key={getPackageKey(pkg?.code, pkg?.isAutoRenew)}
              onClick={() => onSelectPackage(pkg)}
              data-id="option-plan-duration"
              className={disabledPackageAutoRenewUsing && 'disabled'}
              data-package-code={pkg.code}
              data-duration={getPackageDataDuration(pkg.code, pkg?.isAutoRenew)}
            >
              <div className="flex align-item-center pb-8 pt-8">
                <FormControlLabel
                  value={getPackageKey(pkg?.code, pkg?.isAutoRenew)}
                  control={
                    <CustomRadio
                      sx={{ paddingLeft: '11px', marginTop: '3px' }}
                    />
                  }
                  className={
                    disabledPackageAutoRenewUsing
                      ? 'disabled-checkbox'
                      : 'label-checkbox'
                  }
                  label=""
                />

                <PackageDuration
                  showTooltip={samePackage && !isBuyOnApp && !activeAutoRenew}
                  disabledPackageAutoRenewUsing={disabledPackageAutoRenewUsing}
                  handleShowPopupCannotBuyPackage={
                    handleShowPopupCannotBuyPackage
                  }
                  subscriptionPeriodKey={subscriptionPeriodKey}
                  saleOffPercentageRounded={saleOffPercentageRounded}
                  actualPrice={actualPrice}
                  packageCode={pkg?.code}
                  hasSaleOff={hasSaleOff}
                />
              </div>
            </Box>
          );
        })}
      </RadioGroup>
      <NotificationDialogV3
        name={t('noticePopup')}
        title={t('noticePopup')}
        description={t('goToMyPackage', {
          color: '#000000',
        })}
        open={openPopupCannotBuyPackage}
        onClose={() => setOpenPopupCannotBuyPackage(false)}
        fitWidth
        variant="null"
        actionComponent={
          <Button
            onClick={() => setOpenPopupCannotBuyPackage(false)}
            className="btn-close-popup"
            variant="contained"
          >
            {t('gotIt')}
          </Button>
        }
      />
    </div>
  );
};

const PackageInfo = ({
  packages,
  setPackages,
  usingOrders,
  calculateCharacterPurchase,
  currentNumberCharacters,
  onChangeNumberCharacters,
  onChangeSelectedPackageCode,
  selectedPackageCode,
}) => {
  const history = useHistory();
  const isDubbingRoute = useCheckDubbingRoute();
  const { package: packageState } = useSelector((state) => state.order);
  const dispatch = useDispatch();

  const [selectedPackage, setSelectedPackage] = useState({});
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  const { language } = i18n;
  const location = useLocation();
  const isVietNam = checkVietNam();

  const {
    package: packageCode,
    voucherCode,
    bannerType,
    autoRenew,
    credits: creditsQuery,
    devices: devicesQuery,
    voiceClonings: voiceCloningsQuery,
    concurrentRequestApi: concurrentRequestApiQuery,
    creditsPerCycle: creditsPerCycleQuery,
  } = camelcaseKeys(queryString.parse(location.search) || {});

  const autoRenewParam = useMemo(() => {
    if (autoRenew) {
      try {
        return JSON.parse(autoRenew);
      } catch (e) {
        return null;
      }
    }
    return false;
  }, [autoRenew]);

  const changeRoute = ({
    code,
    isAutoRenew,
    credits,
    devices,
    voiceClonings,
  }) => {
    const search = queryString.stringify({
      package: code,
      autoRenew: Boolean(isAutoRenew),
      voucherCode,
      bannerType,
      ...(credits && { credits }),
      devices: devices || 0,
      voiceClonings: voiceClonings || 0,
      ...(concurrentRequestApiQuery !== undefined && {
        concurrentRequestApi: concurrentRequestApiQuery || 0,
      }),
      creditsPerCycle: creditsPerCycleQuery,
    });
    history.replace({ search });
  };

  const handleSelectPackage = (item) => {
    const newLevelPackage = packages.find(
      (p) => p.code === item?.code && p?.isAutoRenew === item?.isAutoRenew,
    );
    const { defaultCredits, defaultDevices, defaultVoiceClonings } =
      getDefaultValueCustomResource(newLevelPackage?.customResource) || {};

    setSelectedPackage(item);
    dispatch(actions.order.updatePackage(newLevelPackage));

    changeRoute({
      code: item?.code,
      isAutoRenew: item?.isAutoRenew,
      credits: defaultCredits,
      devices: defaultDevices,
      voiceClonings: defaultVoiceClonings,
    });
  };

  const fetchPackages = async () => {
    setLoading(true);
    const res = await apis.packages.getPackages({
      type: isDubbingRoute ? PACKAGE_TYPE.DUBBING : PACKAGE_TYPE.STUDIO,
      purchasable: true,
    });
    if (res?.status) {
      const { result = [] } = res;
      const packageList = [];

      result?.packages.forEach((item) => {
        if (!item.active) return;
        if ([PACKAGE_LEVEL.TRIAL, PACKAGE_LEVEL.BASIC].includes(item.level))
          return;
        if (item.originalPackageCode) {
          const originalPackage = result?.packages.find(
            (pkgItem) => pkgItem.code === item.originalPackageCode,
          );
          if (originalPackage) packageList.push({ ...item, originalPackage });
          return;
        }

        // Apply custom pricing for credit packages
        if (item.code === packageCode) {
          let creditOption = item?.cycle?.creditOptions?.find(
            (option) => option?.creditsPerCycle === +creditsPerCycleQuery,
          );
          // If no creditsPerCycleQuery, default to the lowest creditsPerCycle
          if (!creditsPerCycleQuery && item?.cycle?.creditOptions?.length)
            creditOption = item.cycle.creditOptions.reduce((min, option) =>
              option.creditsPerCycle < min.creditsPerCycle ? option : min,
            );
          packageList.push({
            ...item,
            price: creditOption?.vndPrice,
            usdPrice: creditOption?.usdPrice,
            monthlyPrice: creditOption?.vndMonthlyPrice,
            usdMonthlyPrice: creditOption?.usdMonthlyPrice,
            ...(item?.cycle && {
              cycle: {
                ...item?.cycle,
                creditsPerCycle: creditOption?.creditsPerCycle,
              },
            }),
          });
          return;
        }

        packageList.push(item);
      });

      const subscriptions = packageList?.reduce((acc, pkg) => {
        acc.push({ ...pkg, isAutoRenew: false });

        const hasAutoRenew = pkg?.autoRenew && pkg?.autoRenew?.active;
        if (hasAutoRenew && isVietNam) {
          const { usdPrice, vndPrice } = pkg.autoRenew;
          let creditOption = pkg?.cycle?.creditOptions?.find(
            (option) => option?.creditsPerCycle === +creditsPerCycleQuery,
          );
          // If no creditsPerCycleQuery, default to the lowest creditsPerCycle
          if (!creditsPerCycleQuery && pkg?.cycle?.creditOptions?.length)
            creditOption = pkg.cycle.creditOptions.reduce((min, option) =>
              option.creditsPerCycle < min.creditsPerCycle ? option : min,
            );
          acc.push({
            ...pkg,
            isAutoRenew: true,
            price: creditOption?.autoRenewVndPrice || vndPrice,
            monthlyPrice: creditOption?.autoRenewVndPrice || vndPrice,
            usdPrice,
            usdMonthlyPrice: usdPrice,
            ...(pkg?.cycle && {
              cycle: {
                ...pkg?.cycle,
                creditsPerCycle: creditOption?.creditsPerCycle,
              },
            }),
          });
        }

        return acc;
      }, []);

      setPackages(subscriptions);

      let packageSelected;
      if (selectedPackageCode) {
        packageSelected = subscriptions?.find(
          (item) => item.code === selectedPackageCode,
        );
      } else {
        packageSelected = subscriptions?.find(
          (item) =>
            item.code === packageCode && item?.isAutoRenew === autoRenewParam,
        );
      }

      if (packageSelected) {
        setSelectedPackage(packageSelected);
        dispatch(actions.order.updatePackage(packageSelected));
      }
      if (selectedPackageCode) onChangeSelectedPackageCode('');
    }
    setLoading(false);
  };

  const sameLevelPackages = packages
    .filter(
      (pkg) =>
        pkg?.level === packageState?.level &&
        !pkg?.isTopUp &&
        pkg?.expiresIn === packageState?.expiresIn,
    )
    .sort((pkg1, pkg2) => pkg1.expiresIn - pkg2.expiresIn);

  const getPrice = (pkg) => {
    const otherPackage = sameLevelPackages.find(
      (samePkg) => samePkg?.isAutoRenew !== pkg?.isAutoRenew,
    );
    const pricePackage = isVietNam ? pkg?.price : pkg?.usdPrice;
    const priceOtherPackage = isVietNam
      ? otherPackage?.price
      : otherPackage?.usdPrice;

    const saleOffPercentage = (1 - pricePackage / priceOtherPackage) * 100;
    const saleOffPercentageRounded = roundSaleOffPercentage(saleOffPercentage);

    return {
      actualPrice: pricePackage,
      saleOffPercentageRounded,
    };
  };

  const getOptionsResource = (ranges, step) => {
    const options = [];
    (ranges || []).forEach((range) => {
      for (let value = range.min; value <= range.max; value += step) {
        options.push(value.toString());
      }
    });

    return options || [];
  };

  const validateResourceOption = (resourcePath, queryValue) => {
    let prices = packageState?.customResource?.[resourcePath]?.prices;
    if (
      [RESOURCES_TYPE.DEVICES, RESOURCES_TYPE.VOICE_CLONINGS].includes(
        resourcePath,
      )
    ) {
      prices = prices.map((p) => ({
        ...p,
        min: 0,
      }));
    }
    const options = getOptionsResource(
      prices,
      packageState?.customResource?.[resourcePath]?.step,
    );
    if (!options.includes(queryValue)) history.replace(location.pathname);
  };

  // Check package exist and replace history
  const validatePackageAndResources = (type, typeString) => {
    if (location.pathname.includes(typeString)) {
      const chosenPackage = packages?.find(
        (item) => item.code === packageCode && item.type === type,
      );
      if (!chosenPackage) {
        history.replace(location.pathname);
        return;
      }

      const packageCreditsPerCycleOptions =
        chosenPackage?.cycle?.creditOptions || [];

      const matchedOption = packageCreditsPerCycleOptions.find(
        (opt) => opt?.creditsPerCycle === Number(creditsPerCycleQuery),
      );

      const isCyclePackage = Boolean(chosenPackage?.cycle?.totalCycles);
      const hasCreditsQuery = Boolean(creditsPerCycleQuery);
      if (isCyclePackage && hasCreditsQuery && !matchedOption) {
        history.replace(location.pathname);
        return;
      }

      const isCustomPackage = checkCustomPackage({
        type: packageState.type,
        level: packageState.level,
      });
      if (!isCustomPackage) return;

      // If package is custom, validate resource options
      validateResourceOption('credits', creditsQuery);
      validateResourceOption('devices', devicesQuery);
      validateResourceOption('voiceClonings', voiceCloningsQuery);
    }
  };

  useEffect(() => {
    // check complete load list packages
    if (!loading) {
      validatePackageAndResources(PACKAGE_TYPE.DUBBING, 'dubbing');
      validatePackageAndResources(PACKAGE_TYPE.STUDIO, 'studio');
    }
  }, [loading, packageCode, creditsQuery, devicesQuery, voiceCloningsQuery]);

  useEffect(() => {
    fetchPackages();
  }, [selectedPackageCode, isVietNam]);

  if (packageState?.isTopUp)
    return (
      <BuyExtracharacters
        calculateCharacterPurchase={calculateCharacterPurchase}
        onChangeNumberCharacters={onChangeNumberCharacters}
        currentNumberCharacters={currentNumberCharacters}
        packageCode={packageCode}
        packages={packages}
      />
    );

  const PackageContent = () => {
    const isCustomPackage = checkCustomPackage({
      type: packageState.type,
      level: packageState.level,
    });

    return isCustomPackage ? (
      <CustomResources />
    ) : (
      <PackageDurationSelect
        usingOrders={usingOrders}
        sameLevelPackages={sameLevelPackages}
        getPrice={getPrice}
        onSelectPackage={handleSelectPackage}
        selectedPackage={selectedPackage}
      />
    );
  };

  return (
    <StyledPackageInfo>
      {loading ? (
        <Box className="package-header vertical-center">
          <Skeleton
            animation="wave"
            variant="circular"
            width={48}
            height={48}
            sx={{ bgcolor: COLOR.grayV2[100] }}
          />
          <Skeleton
            animation="wave"
            width={150}
            height={48}
            sx={{ bgcolor: COLOR.grayV2[100] }}
          />
        </Box>
      ) : (
        <Box className="package-header vertical-center">
          <Box display="flex" gap="6px" data-id="open-package-type">
            <div className="vertical-center">
              <Typography className="package-name">
                {packageState?.name &&
                  (packageState?.name[language] ||
                    packageState?.name[LANGUAGE.EN])}
              </Typography>
            </div>
          </Box>
          <Box />
        </Box>
      )}
      <Box>
        {loading ? (
          <Skeleton
            animation="wave"
            width="100%"
            height={210}
            sx={{ bgcolor: COLOR.grayV2[100] }}
          />
        ) : (
          <PackageContent />
        )}
      </Box>
    </StyledPackageInfo>
  );
};

export default PackageInfo;
